import { useState } from 'react';

import { notify } from '../shared';
import { useModal } from './useModal';
import { useConfirm } from './useConfirm';
import { useRequest } from './useRequest';

type UseAddEditParams<ItemType extends {}, AddType, EditType> = {
  name: string;
  onAdd?: (payload: AddType) => void;
  onEdit?: (id: string, payload: EditType) => void;
  onDelete?: (id: string) => void;
  onRefresh: () => void;

  nameKey?: keyof ItemType & string;
};

const maybeAppendDetails = (ex: unknown) => {
  if (!ex || typeof ex !== 'object' || !('body' in ex)) return '';

  const { body } = ex;
  if (!body || typeof body !== 'object' || !('detail' in body)) return '';

  const { detail } = body;
  if (typeof detail !== 'string' || !detail.length) return '';

  return `\nDetails: ${detail}`;
};

export const useActions = <
  ItemType extends { id: string; name?: string },
  AddType,
  EditType,
>({
  name,
  onAdd,
  onEdit,
  onDelete,
  onRefresh,
  nameKey,
}: UseAddEditParams<ItemType, AddType, EditType>) => {
  const [item, setItem] = useState<ItemType>();

  const { isOpened, onOpen, onClose } = useModal();
  const handleClose = () => {
    setItem(undefined);
    onClose();
  };
  const [performAdd, adding] = useRequest(
    (_signal, payload: AddType) => onAdd?.(payload),
    {
      deps: [name],
      onFinally: onRefresh,
      onError: ex => {
        return notify.error(`Failed to save ${name}!` + maybeAppendDetails(ex));
      },
      onSuccess: () => {
        notify.success(`${name} saved!`);
        handleClose();
      },
    }
  );

  const handleStartAdd = () => {
    setItem(undefined);
    onOpen();
  };
  const handleAdd = (payload: Partial<AddType>) => {
    performAdd(payload as AddType);
  };

  const [performUpdate, editing] = useRequest(
    (_signal, payload: EditType) => onEdit?.(item?.id!, payload),
    {
      deps: [item?.id, name],
      onFinally: onRefresh,
      onError: ex =>
        notify.error(`Failed to update ${name}!` + maybeAppendDetails(ex)),
      onSuccess: () => {
        notify.success(`${name} updated!`);
        handleClose();
      },
    }
  );
  const handleStartEdit = (item: ItemType) => {
    setItem(item);
    onOpen();
  };
  const handleEdit = (payload: Partial<EditType>) => {
    performUpdate(payload as EditType);
  };

  const [performDelete, deleting] = useRequest(
    (_signal, id: string) => onDelete?.(id),
    {
      deps: [name],
      onFinally: onRefresh,
      onError: ex =>
        notify.error(`Failed to delete ${name}!` + maybeAppendDetails(ex)),
      onSuccess: () => {
        notify.success(`${name} deleted!`);
      },
    }
  );
  const { element, open } = useConfirm();
  const handleStartDelete = (item: ItemType) => {
    const itemName = nameKey ? item[nameKey] : item.name || item.id || '';

    open({
      text: `Are you sure you want to delete ${name} "${itemName}"?`,
      onConfirm: () => performDelete(item.id),
    });
  };

  const updating = adding || editing || deleting;

  return {
    element,

    isOpened,
    updating,
    initialValues: item,

    handleAdd,
    handleStartAdd,

    handleEdit,
    handleStartEdit,

    handleClose,
    handleStartDelete,
  };
};
